import React, { useCallback, useEffect, useState } from "react";
import {
    Box,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import ApiService from "../../../services/ApiService";
import Logger from "../../../services/Logger";
import StatsCrossService from "../../../services/StatsCrossService";
import StatsGridService from "../../../services/StatsGridService";
import UtilService from "../../../services/UtilService";
import PlayStatistics from "./PlayStatistics";

/**
 * Admin user Play Record tab — session stats by product app + legacy grid play stats.
 */
export default function PlayRecordAdmin({ userId, cloudUserId }) {
    const statsUserId = cloudUserId || userId;
    const [byApp, setByApp] = useState(null);
    const [wordWheelJourney, setWordWheelJourney] = useState(null);
    const [gridPlayStats, setGridPlayStats] = useState(null);
    const [crossPlayStats, setCrossPlayStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [journeyError, setJourneyError] = useState(null);

    const load = useCallback(async () => {
        if (!statsUserId) return;
        setLoading(true);
        setError(null);
        setJourneyError(null);
        try {
            const [byAppData, journeyData, gridData, crossData] = await Promise.all([
                ApiService.getData(`/v1/stats/user/${statsUserId}/plays-by-app`).catch((err) => {
                    Logger.warn("plays-by-app failed:", err);
                    return null;
                }),
                ApiService.getData(`/v1/stats/user/${statsUserId}/word-wheel-journey`)
                    .then((data) => {
                        Logger.log("word-wheel-journey:", data);
                        // getData does not throw on HTTP errors — detect Spring error bodies.
                        if (
                            data == null
                            || (typeof data.status === "number" && data.status >= 400)
                            || (data.error && !Array.isArray(data.seasons))
                        ) {
                            const msg =
                                data?.message
                                || data?.error
                                || (data?.status === 404
                                    ? "Journey API not found — restart puzzle-be"
                                    : "Failed to load Word Wheel level");
                            setJourneyError(msg);
                            return null;
                        }
                        return data;
                    })
                    .catch((err) => {
                        Logger.warn("word-wheel-journey failed:", err);
                        setJourneyError(err?.message || "Failed to load Word Wheel level");
                        return null;
                    }),
                StatsGridService.getPlayStatistics(statsUserId, "hw").catch((err) => {
                    Logger.warn("grid play stats failed:", err);
                    return null;
                }),
                StatsCrossService.getPlayStatistics(statsUserId).catch((err) => {
                    Logger.warn("cross play stats failed:", err);
                    return null;
                }),
            ]);
            setByApp(byAppData);
            setWordWheelJourney(journeyData);
            setGridPlayStats(gridData);
            setCrossPlayStats(crossData);
        } catch (err) {
            setError(err?.message || "Failed to load play record");
            Logger.error("PlayRecordAdmin load:", err);
        } finally {
            setLoading(false);
        }
    }, [statsUserId]);

    useEffect(() => {
        load();
    }, [load]);

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Typography color="error" sx={{ mb: 2 }}>
                {error}
            </Typography>
        );
    }

    const apps = Array.isArray(byApp?.apps) ? byApp.apps : [];
    const total = byApp?.total ?? null;
    const webDetail = byApp?.webDetail ?? null;
    const journeySeasons = Array.isArray(wordWheelJourney?.seasons)
        ? wordWheelJourney.seasons
        : Array.isArray(wordWheelJourney?.data?.seasons)
          ? wordWheelJourney.data.seasons
          : [];
    const wordWheelMax = Number(wordWheelJourney?.maxLevel ?? wordWheelJourney?.data?.maxLevel) || 1100;
    const wordWheelDefaultSeason =
        wordWheelJourney?.defaultSeason ??
        wordWheelJourney?.data?.defaultSeason ??
        "season_2026";

    const wordWheelLevel = (() => {
        const candidates = [];
        const top = Number(wordWheelJourney?.level ?? wordWheelJourney?.data?.level);
        if (Number.isFinite(top) && top > 0) candidates.push(top);
        for (const row of journeySeasons) {
            const n = Number(row?.level);
            if (Number.isFinite(n) && n > 0) candidates.push(n);
        }
        if (candidates.length === 0) return null;
        const defaultRow = journeySeasons.find(
            (s) => String(s?.season || "").trim().toLowerCase() === wordWheelDefaultSeason.toLowerCase(),
        );
        const fromDefault = Number(defaultRow?.level);
        if (Number.isFinite(fromDefault) && fromDefault > 0) return fromDefault;
        return Math.max(...candidates);
    })();

    const wordWheelUpdatedAt = (() => {
        const defaultRow = journeySeasons.find(
            (s) => String(s?.season || "").trim().toLowerCase() === wordWheelDefaultSeason.toLowerCase(),
        );
        const ts = defaultRow?.updatedAt ?? journeySeasons[0]?.updatedAt ?? null;
        return ts != null ? UtilService.formatDate(ts) || null : null;
    })();

    return (
        <Box>
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h5" gutterBottom>
                        Word Wheel Quest
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Main journey progress for this user.
                    </Typography>

                    {journeyError ? (
                        <Typography color="error" variant="body2" sx={{ mb: 2 }}>
                            {journeyError}
                        </Typography>
                    ) : null}

                    <Box
                        sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 3,
                            alignItems: "center",
                            p: 2.5,
                            bgcolor: "action.hover",
                            borderRadius: 1,
                        }}
                    >
                        <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                Level
                            </Typography>
                            <Typography
                                component="div"
                                sx={{
                                    fontSize: { xs: "2.75rem", sm: "3.25rem" },
                                    fontWeight: 800,
                                    lineHeight: 1,
                                    color: "primary.main",
                                    letterSpacing: "-0.02em",
                                }}
                            >
                                {wordWheelLevel != null ? wordWheelLevel : "—"}
                            </Typography>
                        </Box>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                            <Chip size="small" label={wordWheelDefaultSeason} variant="outlined" />
                            <Typography variant="body2" color="text.secondary">
                                of {wordWheelMax} max
                            </Typography>
                            {wordWheelUpdatedAt ? (
                                <Typography variant="body2" color="text.secondary">
                                    Updated {wordWheelUpdatedAt}
                                </Typography>
                            ) : null}
                            {wordWheelLevel == null ? (
                                <Typography variant="caption" color="text.secondary">
                                    No journey level found for this cloud user.
                                </Typography>
                            ) : null}
                        </Box>
                    </Box>
                </CardContent>
            </Card>

            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h5" gutterBottom>
                        Play by app
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Session counts from each product&apos;s play tables (started / completed).
                    </Typography>

                    {total ? (
                        <Box
                            sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 3,
                                mb: 2.5,
                                p: 2,
                                bgcolor: "action.hover",
                                borderRadius: 1,
                            }}
                        >
                            <Box>
                                <Typography variant="body2" color="text.secondary">
                                    Total plays
                                </Typography>
                                <Typography variant="h6" fontWeight="bold">
                                    {total.countPlay ?? 0}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="body2" color="text.secondary">
                                    Completed
                                </Typography>
                                <Typography variant="h6" fontWeight="bold" color="success.main">
                                    {total.countFinished ?? 0}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="body2" color="text.secondary">
                                    Incompleted
                                </Typography>
                                <Typography variant="h6" fontWeight="bold" color="error.main">
                                    {Math.max(0, (total.countPlay ?? 0) - (total.countFinished ?? 0))}
                                </Typography>
                            </Box>
                        </Box>
                    ) : null}

                    {apps.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                            No per-app play data available.
                        </Typography>
                    ) : (
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>App</TableCell>
                                        <TableCell align="right">Level</TableCell>
                                        <TableCell align="right">Score</TableCell>
                                        <TableCell align="right">Plays</TableCell>
                                        <TableCell align="right">Completed</TableCell>
                                        <TableCell align="right">Incompleted</TableCell>
                                        <TableCell align="right">Completion</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {apps.map((row) => (
                                        <TableRow key={row.appCode} hover>
                                            <TableCell>
                                                <Typography fontWeight={600}>
                                                    {row.label || row.appCode}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {row.appCode}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                {row.appCode === "word_wheel_quest" ? (
                                                    <Typography fontWeight={800} color="primary.main">
                                                        {wordWheelLevel}
                                                    </Typography>
                                                ) : (
                                                    "—"
                                                )}
                                            </TableCell>
                                            <TableCell align="right">{row.score ?? 0}</TableCell>
                                            <TableCell align="right">{row.countPlay ?? 0}</TableCell>
                                            <TableCell align="right">{row.countFinished ?? 0}</TableCell>
                                            <TableCell align="right">
                                                {row.countIncompleted ??
                                                    Math.max(0, (row.countPlay ?? 0) - (row.countFinished ?? 0))}
                                            </TableCell>
                                            <TableCell align="right">
                                                {row.completionRate ?? 0}%
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}

                    {webDetail ? (
                        <Box sx={{ mt: 3 }}>
                            <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                                Web breakdown
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Box sx={{ p: 1.5, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            Grid (hidden message / word search)
                                        </Typography>
                                        <Typography variant="body1">
                                            {(webDetail.grid?.countFinished ?? 0)}/
                                            {(webDetail.grid?.countPlay ?? 0)} completed
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Box sx={{ p: 1.5, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            Crossword
                                        </Typography>
                                        <Typography variant="body1">
                                            {(webDetail.crossword?.countFinished ?? 0)}/
                                            {(webDetail.crossword?.countPlay ?? 0)} completed
                                            {crossPlayStats?.latest ? " · has recent play" : ""}
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Box>
                    ) : null}
                </CardContent>
            </Card>

            <PlayStatistics
                playStats={gridPlayStats}
                userId={userId}
                title="Web grid play statistics"
                onReset={() => {
                    load();
                }}
            />
        </Box>
    );
}
